import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ======================= Thunks =======================

/**
 * Fetch all emergency contacts for a specific employee
 */
export const fetchEmergencyContact = createAsyncThunk(
  'employeeEmergencyContact/fetch',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE}/employee/emergency-contact/employee/${employeeId}`);
      return Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
    } catch (err) {
      if (err.response?.status === 404) return [];
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Create a new emergency contact entry
 */
export const createEmergencyContact = createAsyncThunk(
  'employeeEmergencyContact/create',
  async ({ employeeId, contactData }, { rejectWithValue }) => {
    try {
      const payload = {
        employeeId: employeeId,
        firstName: contactData.firstName,
        middleName: contactData.middleName,
        lastName: contactData.lastName,
        emergencyContact: contactData.emergencyContact || false,
        relationship: contactData.relationship,
        phone: contactData.phone,
        email: contactData.email,
        address: contactData.address
      };

      const response = await axios.post(`${API_BASE}/employee/emergency-contact`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Update an existing emergency contact
 */
export const updateEmergencyContact = createAsyncThunk(
  'employeeEmergencyContact/update',
  async ({ id, contactData }, { rejectWithValue }) => {
    try {
      const payload = {
        employeeId: contactData.employeeId,
        firstName: contactData.firstName,
        middleName: contactData.middleName,
        lastName: contactData.lastName,
        emergencyContact: contactData.emergencyContact,
        relationship: contactData.relationship,
        phone: contactData.phone,
        email: contactData.email,
        address: contactData.address
      };

      const response = await axios.put(`${API_BASE}/employee/emergency-contact/${id}`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Delete an emergency contact
 */
export const deleteEmergencyContact = createAsyncThunk(
  'employeeEmergencyContact/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_BASE}/employee/emergency-contact/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Check Employee Completeness
 * Triggered after save/delete to update overall employee status
 */
export const checkEmployeeCompleteness = createAsyncThunk(
  'employeeEmergencyContact/checkCompleteness',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_BASE}/employee/${employeeId}/check-completeness`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Completeness check failed");
    }
  }
);

// ======================= Slice =======================

const employeeEmergencyContactSlice = createSlice({
  name: 'employeeEmergencyContact',
  initialState: {
    contacts: [],
    status: 'idle',
    completenessStatus: 'idle', // Track completeness separately
    error: null,
  },
  reducers: {
    resetEmergencyContact(state) {
      state.contacts = [];
      state.status = 'idle';
      state.completenessStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* Fetch */
      .addCase(fetchEmergencyContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contacts = action.payload;
      })
      /* Create */
      .addCase(createEmergencyContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contacts.push(action.payload);
      })
      /* Update */
      .addCase(updateEmergencyContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.contacts.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.contacts[index] = action.payload;
        }
      })
      /* Delete */
      .addCase(deleteEmergencyContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contacts = state.contacts.filter(c => c.id !== action.payload);
      })
      /* Completeness Check */
      .addCase(checkEmployeeCompleteness.pending, (state) => {
        state.completenessStatus = 'loading';
      })
      .addCase(checkEmployeeCompleteness.fulfilled, (state) => {
        state.completenessStatus = 'succeeded';
      })
      .addCase(checkEmployeeCompleteness.rejected, (state, action) => {
        state.completenessStatus = 'failed';
        state.error = action.payload;
      })
      /* Global Loading/Error Matchers */
      .addMatcher(
        (action) => action.type.endsWith('/pending') && !action.type.includes('checkCompleteness'),
        (state) => { state.status = 'loading'; state.error = null; }
      )
      .addMatcher(
        (action) => action.type.endsWith('/rejected') && !action.type.includes('checkCompleteness'),
        (state, action) => {
          state.status = 'failed';
          state.error = action.payload;
        }
      );
  },
});

export const { resetEmergencyContact } = employeeEmergencyContactSlice.actions;
export default employeeEmergencyContactSlice.reducer;